import OrderJson from './interfaces/OrderJson'
import Customer from './Customer'
import { OrderState } from './enums/OrderState'
import { OrderCheckoutState } from './enums/OrderCheckoutState'
import { OrderPaymentState } from './enums/OrderPaymentState'
import Location from '../event/Location'
import Money from '../shared/Money'
import { OrderItem } from './OrderItem'
import { DateTime } from 'luxon'
import {
  OrderItemFinalStates,
  OrderItemState,
} from 'src/models/order/enums/OrderItemState'

export default class Order {
  id: string
  customer: Customer | null
  token: string
  state: OrderState
  checkoutState: OrderCheckoutState
  paymentState: OrderPaymentState
  location: Location
  orderNumber: string | null
  itemsTotal: Money
  adminCost: Money
  total: Money
  cartExpirationDate: DateTime | null
  paymentExpirationDate: DateTime | null
  pendingPayment: boolean
  failedPayment: boolean
  items: Array<OrderItem>

  constructor(
    id: string,
    customer: Customer | null,
    token: string,
    state: OrderState,
    checkoutState: OrderCheckoutState,
    paymentState: OrderPaymentState,
    location: Location,
    orderNumber: string | null,
    itemsTotal: Money,
    adminCost: Money,
    total: Money,
    cartExpirationDate: DateTime | null,
    paymentExpirationDate: DateTime | null,
    hasPendingPayment: boolean,
    hasFailedPayment: boolean,
    items: Array<OrderItem>,
  ) {
    this.id = id
    this.customer = customer
    this.token = token
    this.state = state
    this.checkoutState = checkoutState
    this.paymentState = paymentState
    this.location = location
    this.orderNumber = orderNumber
    this.itemsTotal = itemsTotal
    this.adminCost = adminCost
    this.total = total
    this.cartExpirationDate = cartExpirationDate
    this.paymentExpirationDate = paymentExpirationDate
    this.pendingPayment = hasPendingPayment
    this.failedPayment = hasFailedPayment
    this.items = items
  }

  isCart() {
    return this.state === OrderState.STATE_CART
  }

  isCancelled() {
    return this.state === OrderState.STATE_CANCELLED
  }

  isFulfilled() {
    return this.state === OrderState.STATE_FULFILLED
  }

  isExpired() {
    return this.checkoutState === OrderCheckoutState.STATE_EXPIRED
  }
  isPaid() {
    return this.paymentState === OrderPaymentState.STATE_PAID
  }

  isAwaitingPayment() {
    return this.paymentState === OrderPaymentState.STATE_AWAITING_PAYMENT
  }

  hasPendingPayment() {
    return this.pendingPayment
  }

  hasFailedPayment() {
    return this.failedPayment
  }

  hasNonFinalItems() {
    for (const item of this.items as Array<OrderItem>) {
      if (!OrderItemFinalStates.includes(item.state)) {
        return true
      }
    }

    return false
  }

  hasAllFinalItems() {
    return this.items.every((item) => OrderItemFinalStates.includes(item.state))
  }

  hasAtLeastOneOrderItemFulfilled() {
    for (const item of this.items as Array<OrderItem>) {
      if (item.state === OrderItemState.STATE_FULFILLED) {
        return true
      }
    }

    return false
  }

  hasOneItem() {
    return this.items?.length === 1
  }

  static fromJson(data: OrderJson) {
    return new this(
      data.id,
      data.customer ? Customer.fromJson(data.customer) : null,
      data.token,
      data.state,
      data.checkoutState,
      data.paymentState,
      Location.fromJson(data.location),
      data.orderNumber,
      Money.fromJson(data.itemsTotal),
      Money.fromJson(data.adminCost),
      Money.fromJson(data.total),
      data.cartExpirationDate
        ? DateTime.fromISO(data.cartExpirationDate.date, {
            zone: data.cartExpirationDate.timezone,
          })
        : null,
      data.paymentExpirationDate
        ? DateTime.fromISO(data.paymentExpirationDate.date, {
            zone: data.paymentExpirationDate.timezone,
          })
        : null,
      data.hasPendingPayment,
      data.hasFailedPayment,
      data.items.map((item) => OrderItem.fromJson(item)),
    )
  }

  toJson(): OrderJson {
    return {
      id: this.id,
      customer: this.customer ? this.customer : null,
      token: this.token,
      state: this.state,
      checkoutState: this.checkoutState,
      paymentState: this.paymentState,
      location: this.location.toJson(),
      orderNumber: this.orderNumber,
      itemsTotal: this.itemsTotal.toJson(),
      adminCost: this.adminCost.toJson(),
      total: this.total.toJson(),
      cartExpirationDate: this.cartExpirationDate
        ? {
            date: this.cartExpirationDate.toISO() ?? '',
            timezone: this.cartExpirationDate.zoneName ?? '',
          }
        : null,
      paymentExpirationDate: this.paymentExpirationDate
        ? {
            date: this.paymentExpirationDate.toISO() ?? '',
            timezone: this.paymentExpirationDate.zoneName ?? '',
          }
        : null,
      hasPendingPayment: this.pendingPayment,
      hasFailedPayment: this.failedPayment,
      items: this.items.map((item) => item.toJson()),
    }
  }
}
